import {
  Button,
  Callout,
  Card,
  Divider,
  FormGroup,
  HTMLSelect,
  NumericInput,
} from '@blueprintjs/core'

import type { Store } from '../store'

/**
 * Cell → Tool panel.
 *
 * Shows the per-cell tool library (seeded with Panasonic test torches
 * in Phase 6), lets the user pick an active tool, and gates RAPID
 * export on mass_kg being set.
 *
 * Phase 7 adds the Import STEP flow and mount-frame gizmo.
 */
export function ToolPanel({ store }: { store: Store }) {
  const active = store.job.cell.tool
  const library = store.toolLibrary

  return (
    <div className="pm">
      <div className="pm-header">
        <span className="pm-title">Cell / Tool</span>
      </div>
      <Divider />
      <div className="pm-body">
        <FormGroup label="Active tool">
          <HTMLSelect
            fill
            value={active?.id ?? ''}
            onChange={(e) => store.setActiveTool(e.target.value || null)}
          >
            <option value="">— none —</option>
            {library.map((t) => (
              <option key={t.id} value={t.id}>
                {t.vendor} {t.part} ({t.description.slice(0, 40)})
              </option>
            ))}
          </HTMLSelect>
        </FormGroup>

        {active && (
          <Card compact style={{ marginBottom: 12 }}>
            <div>
              <b>{active.vendor}</b> {active.part}
            </div>
            <div className="tree-dim">{active.description}</div>
            <ul style={{ paddingLeft: 18, marginTop: 8 }}>
              <li>TCP: ({active.tcp.pos.map((n) => n.toFixed(1)).join(', ')}) mm</li>
              <li>Neck angle: {active.neck.angle_to_flange_axis_deg.toFixed(1)}°</li>
              <li>Stickout: {active.stickout_mm.toFixed(1)} mm</li>
              <li>Mount PCD: {active.mount.bolt_pcd_mm.toFixed(1)} mm</li>
            </ul>
            <FormGroup
              label="Mass (kg)"
              helperText={
                active.mass_kg == null
                  ? 'Required before RAPID export — CAD does not carry mass.'
                  : 'Vendor-published or scale-measured value.'
              }
              intent={active.mass_kg == null ? 'danger' : 'none'}
            >
              <NumericInput
                min={0}
                stepSize={0.1}
                value={active.mass_kg ?? ''}
                placeholder="e.g. 1.8"
                onValueChange={(v) =>
                  store.updateTool(active.id, { mass_kg: Number.isFinite(v) ? v : null })
                }
              />
            </FormGroup>
            {active.mass_kg == null && (
              <Callout intent="warning" icon="warning-sign">
                RAPID export is blocked until mass is set.
              </Callout>
            )}
            {active.id.startsWith('panasonic_') && (
              <Callout intent="warning" icon="info-sign" style={{ marginTop: 8 }}>
                Panasonic test torch — NOT the SORS cell torch. Use for layout
                checks only.
              </Callout>
            )}
          </Card>
        )}

        <Divider />
        <Button
          icon="import"
          text="Import STEP…"
          intent="primary"
          onClick={() => store.openToolImport()}
        />
      </div>
    </div>
  )
}
